<template>
  <div>
    <ToastContainer />
    <Loader v-if="showLoader" />
    <AnalyticsConsentBanner />
    <component :is="currentLayout" v-if="isRouterLoaded && isLDReady">
      <router-view />
    </component>
    <MobileOrientationGuard />
    <!-- Payment record (PE-1019): one stacked drawer for the whole app, opened by payment id -->
    <PaymentRecordDrawer
      v-if="isRouterLoaded && isLDReady && featureFlagsStore.paymentLifecycleEnabled"
    />
    <!-- Network retry -->
    <Dialog
      :open="dialogVisible"
      hide-close-button
      size="sm"
      data-test-id="network-retry-dialog"
      :title="networkRetryTitle"
    >
      <div class="flex flex-col items-center justify-center text-center">
        <template v-if="!networkStore.showButton">
          <Spinner class="mb-4" />
          <div class="text-txt-700 type-body-2 whitespace-pre-wrap">
            {{ t('components.networkRetryDialog.content') }}
          </div>
          <div class="type-body-2 mt-2">
            {{
              t('components.networkRetryDialog.attempt', {
                attempt: networkStore.retryCount,
                maxAttempts: 3,
              })
            }}
          </div>
        </template>
        <div v-else class="text-txt-700 type-body-2 whitespace-pre-wrap">
          {{ t('components.networkRetryDialog.failureContent') }}
        </div>
      </div>
      <template v-if="networkStore.showButton" #footer>
        <div class="flex justify-center">
          <Button class="w-32" size="lg" data-cy="reload-page-button" @click="refreshPage">
            {{ t('components.networkRetryDialog.reload') }}
          </Button>
        </div>
      </template>
    </Dialog>
    <!-- Version conflict -->
    <!-- Elevated: a 409 is raised from an interceptor, so this lands on top of whatever dialog
         the user was mid-action in. At the normal layer it renders behind and the click that
         caused it reads as a dead click. -->
    <Dialog
      :open="versionConflictDialogVisible"
      hide-close-button
      elevated
      size="sm"
      data-test-id="version-conflict-dialog"
      :title="t('components.versionConflictDialog.title')"
    >
      <div class="text-txt-700 type-body-2 text-center whitespace-pre-wrap">
        {{ t('components.versionConflictDialog.content') }}
      </div>
      <template #footer>
        <div class="flex justify-center">
          <Button size="lg" data-cy="version-conflict-reload-button" @click="refreshPage">
            {{ t('components.versionConflictDialog.reload') }}
          </Button>
        </div>
      </template>
    </Dialog>
    <RoleEventDialog />
    <!-- Organisation switch -->
    <Dialog
      :open="changeOrgDialogVisible"
      hide-close-button
      size="sm"
      data-test-id="change-org-loading-modal"
      :title="t('navigation.orgChangeModal.title')"
    >
      <div class="flex flex-col items-center text-center">
        <Spinner size="medium" />
        <span class="text-txt-700 type-body-1 mt-4">{{
          t('navigation.orgChangeModal.content')
        }}</span>
      </div>
    </Dialog>
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import i18n from '@/plugins/vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useLDClient, useLDFlag, useLDReady } from 'launchdarkly-vue-client-sdk';
import {
  computed,
  DefineComponent,
  defineAsyncComponent,
  onMounted,
  onUnmounted,
  watch,
} from 'vue';

import Spinner from '@/components/Utilities/Spinner.vue';

// UI Library
import { Button, Dialog } from '@boqnet/ui';

import baseLayout from '@/layouts/BaseLayout.vue';
import authLayout from '@/layouts/AuthLayout.vue';
import errorLayout from '@/layouts/ErrorLayout.vue';
import defaultLayout from '@/layouts/DefaultLayout.vue';
import BOQAdminLayout from '@/layouts/BOQAdminLayout.vue';
import navigationOnly from '@/layouts/NavigationOnlyLayout.vue';
import boqPageLayout from '@/layouts/BoqPageLayout.vue';
import ToastContainer from '@/components/Utilities/ToastContainer.vue';

import Loader from '@/components/Utilities/Loader.vue';
import RoleEventDialog from '@/components/RoleEventDialog.vue';
import AnalyticsConsentBanner from '@/components/Analytics/AnalyticsConsentBanner.vue';
import MobileOrientationGuard from '@/components/MobileOrientationGuard.vue';
import { useBoqStore } from '@/stores/boqs.store';
import { useAuthStore } from '@/stores/auth.store';
import { useSiteStore } from '@/stores/site.store';
import { usePusherStore } from '@/stores/pusher.store';
import { useNetworkStore } from '@/stores/network.store';
import { useOrgStore } from '@/stores/organisations.store';
import { refreshPermissionsOnBoot } from '@/utils/session.utils';
import { useLoaderStore } from '@/stores/utils/loader.store';
import { useFeatureFlagsStore } from '@/stores/feature-flags.store';
import { usePermissionStore } from '@/stores/permission.store';
import { useLaunchDarklyIdentity } from '@/composables/use-launch-darkly-identity';
import { useCan } from '@/composables/use-can';
import { Actions, Subjects } from '@boqnet/permissions';
import { useBreadcrumbStore } from '@/stores/utils/breadcrumb.store';
import { useVersionConflictStore } from '@/stores/utils/version-conflict.store';

import { isRouteBlockedByAbility } from '@/common/utils/route-permission.utils';
import { isStrandedLanding, refusedRouteRedirect } from '@/common/utils/landing.utils';
import { selectedOrgId } from '@/common/utils/role.utils';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import {
  EntityTypeEnum,
  RoleEventEnum,
  RoleMessageData,
  StatusMessageData,
} from '@/types/plugins/pusher.types';

import {
  handleOrgRoleAdded,
  handleOrgRoleChanged,
  handleOrgRoleRemoved,
  refreshAccessAfterRoleEvent,
  handleProjectRoleAdded,
  handleProjectRoleChanged,
  handleProjectRoleRemoved,
  handleSiteRoleAdded,
  handleSiteRoleChanged,
  handleSiteRoleRemoved,
  joinAppChannel,
} from '@/common/utils/pusher.utils';

const isLDReady = useLDReady();
const ldClient = useLDClient();

const orgStore = useOrgStore();
const boqStore = useBoqStore();
const authStore = useAuthStore();
const siteStore = useSiteStore();
const pusherStore = usePusherStore();
const loaderStore = useLoaderStore();
const { loggedInUser } = storeToRefs(authStore);
const { connectionStatus } = storeToRefs(pusherStore);

const { t } = i18n.global;
const route = useRoute();

const layouts: Record<string, DefineComponent> = {
  auth: authLayout as unknown as DefineComponent,
  error: errorLayout as unknown as DefineComponent,
  base: baseLayout as unknown as DefineComponent,
  default: defaultLayout as unknown as DefineComponent,
  boqAdmin: BOQAdminLayout as unknown as DefineComponent,
  navigationOnly: navigationOnly as unknown as DefineComponent,
  boq: boqPageLayout as unknown as DefineComponent,
};

const showLoader = computed(() => {
  const loaderRoutes = [
    'confirm-order-requests',
    'create-boq-vendor',
    'create-org',
    'create-client-variation',
  ];
  return loaderRoutes.includes(route.name as string);
});

const networkStore = useNetworkStore();
const versionConflictStore = useVersionConflictStore();

const dialogVisible = computed(() => networkStore.showModal);
const versionConflictDialogVisible = computed(() => versionConflictStore.showModal);

const networkRetryTitle = computed(() =>
  t(
    networkStore.showButton
      ? 'components.networkRetryDialog.failureTitle'
      : 'components.networkRetryDialog.title',
  ),
);

const { can } = useCan();

const refreshPage = () => {
  location.reload();
};

const breadcrumbStore = useBreadcrumbStore();
const { items } = storeToRefs(breadcrumbStore);

const router = useRouter();
const permissionStore = usePermissionStore();
const featureFlagsStore = useFeatureFlagsStore();
// Async: keeps the record and its sub-views out of the entry chunk.
const PaymentRecordDrawer = defineAsyncComponent(
  () => import('@/components/Accounts/Payments/PaymentRecord/PaymentRecordDrawer.vue'),
);
const isUnifiedAuthEnabled = useLDFlag('erp_app_unified_login_v1', false);
const isBulkOrdersEnabled = useLDFlag('erp_app_bulk_orders_v1', false);
const isPaymentLifecycleEnabled = useLDFlag('erp_app_proof_of_payment_ingestion_v1', false);
const isActivityLogsVendorsSitesProjectsEnabled = useLDFlag(
  'erp_app_activity_logs_vendors_sites_projects_v1',
  false,
);
const isRoleCapabilityOverviewEnabled = useLDFlag('erp_app_role_capability_overview_v1', false);
const isBoqCatalogAdminEnabled = useLDFlag('erp_app_boq_catalog_admin_v1', false);
const isProvisionalBudgetsEnabled = useLDFlag('erp_app_provisional_budgets_v1', false);
const isBoqDatabaseVendorsReworkEnabled = useLDFlag(
  'erp_app_boq_database_vendors_rework_v1',
  false,
);
const isBoqLayoutFoundationEnabled = useLDFlag('erp_app_boq_layout_foundation_v1', false);

const centralOrdersAndClaimsMigratedRoutes = new Set([
  'orders',
  'claims',
  'create-new-order',
  'create-new-claim',
  'returns',
  // The Bulk Orders tab is a third child of the same `OrdersLayout`, so it needs the same
  // navigation-only shell its siblings get — without this it renders the default layout and the
  // third tab looks like a different page from the two beside it.
  'bulk-orders',
  'projects',
  'create-site',
  'vendors',
  'vendor-items',
  'add-vendor-item',
  'sites',
]);

const currentLayout = computed(() => {
  const routeName = route.name as string;

  if (centralOrdersAndClaimsMigratedRoutes.has(routeName)) return layouts['navigationOnly'];
  if (route.meta?.layout === 'boqAdmin' && featureFlagsStore.boqDatabaseVendorsReworkEnabled) {
    return layouts['navigationOnly'];
  }
  if (route.meta?.layout === 'boq' && !featureFlagsStore.boqLayoutFoundationEnabled) {
    return defaultLayout;
  }
  return layouts[route.meta?.layout as string] || defaultLayout;
});

const isRouterLoaded = computed(() => {
  return !!route.name;
});

const changeOrgDialogVisible = computed(() => loaderStore.isLoading && orgStore.orgChanged);

const { isIdentified } = useLaunchDarklyIdentity(
  ldClient,
  isLDReady,
  () => loggedInUser.value?.id,
  import.meta.env.VITE_APP_USE_LOCAL_FLAGS === 'true',
);

// Context: the Role capabilities route and view trust this once `areRoleFlagsResolved` is true, so
// it is published here, before the watch below sets that marker.
watch(
  isRoleCapabilityOverviewEnabled,
  (value) => featureFlagsStore.setRoleCapabilityOverviewEnabled(value),
  { immediate: true },
);
// Published before the marker: the refusal redirect below reads it.
watch(isBoqCatalogAdminEnabled, (value) => featureFlagsStore.setBoqCatalogAdminEnabled(value), {
  immediate: true,
});
// The flag or ability can arrive after the landing was chosen.
watch(
  [() => featureFlagsStore.boqCatalogAdminEnabled, () => permissionStore.abilityVersion],
  () => {
    if (isStrandedLanding(route)) {
      router.push({
        name: 'boq-admin-vendors',
        params: selectedOrgId.value ? { organisationId: selectedOrgId.value } : {},
      });
    }
  },
);
// Published here for the same reason: the provisional-budgets availability trusts it once
// `areRoleFlagsResolved` is true.
watch(
  isProvisionalBudgetsEnabled,
  (value) => featureFlagsStore.setProvisionalBudgetsEnabled(value),
  { immediate: true },
);
watch(
  [isLDReady, isIdentified, () => permissionStore.isInitialized],
  ([ldReady, identified]) => {
    // Context: the marker is what lets the router guard start denying routes, so it may only be
    // published once the identify for the signed-in user has settled. Setting it earlier would
    // treat an ability built on unresolved flag defaults, or on the shared boot key's flags, as
    // authoritative (PE-588). A rejected or timed-out identify publishes it on the previous
    // context's flags instead of leaving the ability provisional. With no user or local flags it
    // is published as soon as LaunchDarkly is ready. A future flag-gated role must be published into
    // the store BEFORE this line, for the same reason.
    // Open by owner decision (D90): nothing withdraws it while a later sign-in in this tab is identified.
    if (ldReady && identified) {
      permissionStore.setRoleFlagsResolved(true);
    }

    // The router guard skips the ability check until permissions are initialised. Once they are,
    // re-check the current route so a user who landed on a blocked page before then gets redirected.
    // Context: on a cold load the first navigation can still be pending here, leaving `route` on
    // the start location with no guard meta, so wait for it to land first (PLE-1776).
    if (permissionStore.isInitialized) {
      void router.isReady().then(() => {
        if (!permissionStore.isInitialized || !isRouteBlockedByAbility(route.meta)) return;
        router.push({
          ...refusedRouteRedirect(),
          params: selectedOrgId.value ? { organisationId: selectedOrgId.value } : {},
        });
      });
    }
  },
  { immediate: true },
);
watch(isUnifiedAuthEnabled, (value) => authStore.setUnifiedAuth(value), { immediate: true });
// Its own watch because the combined one above republishes permission state on every run, and
// an unrelated flag has no business triggering that. This flag gates surfaces only — the CASL
// abilities are deliberately not flag-aware (PE-614).
watch(isBulkOrdersEnabled, (value) => featureFlagsStore.setBulkOrdersEnabled(value), {
  immediate: true,
});
watch(isPaymentLifecycleEnabled, (value) => featureFlagsStore.setPaymentLifecycleEnabled(value), {
  immediate: true,
});
watch(
  isActivityLogsVendorsSitesProjectsEnabled,
  (value) => featureFlagsStore.setActivityLogsVendorsSitesProjectsEnabled(value),
  { immediate: true },
);
watch(
  isBoqDatabaseVendorsReworkEnabled,
  (value) => featureFlagsStore.setBoqDatabaseVendorsReworkEnabled(value),
  { immediate: true },
);
watch(
  isBoqLayoutFoundationEnabled,
  (value) => featureFlagsStore.setBoqLayoutFoundationEnabled(value),
  { immediate: true },
);
watch(
  () => route.name,
  () => {
    for (const [index, item] of items.value.entries()) {
      item.index = index;
    }
  },
);

onMounted(async () => {
  // Not awaited: permissions must not gate first render, and everything below is unrelated to
  // them. On the unified-auth handoff the middleware refreshes too, so that one path pays a
  // duplicate fetch once per handoff — cheaper than leaving a whole session stale.
  void refreshPermissionsOnBoot();

  setOptions({ key: import.meta.env.VITE_MAPS_API_KEY as string, v: 'weekly' });
  await importLibrary('places');

  watch(
    connectionStatus,
    (status) => {
      if (status === 'connected') {
        joinAppChannel('boq-events', {
          Boq_Status_Changed: (data) => {
            boqStore.getBOQ(data.SiteId, true);
          },
        });

        joinAppChannel('role-events', {
          Role_Removed: (data) => {
            handleRoleEvent(data, RoleEventEnum.REMOVED);
          },
          Role_Added: (data) => {
            handleRoleEvent(data, RoleEventEnum.ADDED);
          },
          Role_Changed: (data) => {
            handleRoleEvent(data, RoleEventEnum.CHANGED);
          },
        });

        joinAppChannel('site-events', {
          Site_Status_Changed: (data: StatusMessageData) => {
            handleSiteStatusChanged(data);
          },
        });
      } else {
        console.warn(
          `Pusher not connected (${status}). Global channel subscriptions will be attempted upon connection.`,
        );
      }
    },
    { immediate: true },
  );
});

const handleRoleEvent = async (data: RoleMessageData, eventName: RoleEventEnum) => {
  if (!data) {
    console.error('handleRoleEvent: data is undefined or null');
    return;
  }

  if (!loggedInUser.value || data.UserId !== loggedInUser.value.id) {
    return;
  }

  pusherStore.roleMessage = data;

  const activeOrgIdBeforeRefresh = await refreshAccessAfterRoleEvent();

  // If the affected user is currently viewing the org users list, refresh it so
  // their updated role is reflected live instead of showing the stale role until
  // a manual reload. Version-race-safe via the store's orgUsersVersion guard.
  if (
    router.currentRoute.value.name === 'organisation-details' &&
    router.currentRoute.value.hash === '#users'
  ) {
    // Best-effort refresh — must not abort the role-event handlers below if it fails.
    try {
      await orgStore.getCurrentOrgUsers();
    } catch (error) {
      console.error('Failed to refresh organisation users on role event:', error);
    }
  }

  switch (eventName) {
    case RoleEventEnum.REMOVED: {
      switch (data.EntityType) {
        case EntityTypeEnum.ORG: {
          await handleOrgRoleRemoved(data, activeOrgIdBeforeRefresh);
          break;
        }
        case EntityTypeEnum.PROJECT: {
          await handleProjectRoleRemoved(data);
          break;
        }
        case EntityTypeEnum.SITE: {
          await handleSiteRoleRemoved(data);
          break;
        }
        default: {
          console.warn('Unhandled entity type for REMOVED event:', data.EntityType);
        }
      }
      break;
    }

    case RoleEventEnum.CHANGED: {
      switch (data.EntityType) {
        case EntityTypeEnum.ORG: {
          await handleOrgRoleChanged(data);
          break;
        }
        case EntityTypeEnum.PROJECT: {
          await handleProjectRoleChanged(data);
          break;
        }
        case EntityTypeEnum.SITE: {
          await handleSiteRoleChanged(data);
          break;
        }
        default: {
          console.warn('Unhandled entity type for UPDATED event:', data.EntityType);
        }
      }
      break;
    }

    case RoleEventEnum.ADDED: {
      switch (data.EntityType) {
        case EntityTypeEnum.ORG: {
          await handleOrgRoleAdded(data);
          break;
        }
        case EntityTypeEnum.PROJECT: {
          await handleProjectRoleAdded(data);
          break;
        }
        case EntityTypeEnum.SITE: {
          await handleSiteRoleAdded(data);
          break;
        }
        default: {
          console.warn('Unhandled entity type for ADDED event:', data.EntityType);
        }
      }
      break;
    }

    default: {
      console.warn('Unhandled role event:', eventName);
    }
  }
};

const handleSiteStatusChanged = (data: StatusMessageData) => {
  if (!data) {
    console.error('handleSiteStatusChanged: data is undefined or null');
    return;
  }

  if (data.OrgId !== loggedInUser.value?.selectedOrg?.id) {
    return;
  }

  // Context: may this user read the site this event is about? The adapter resolves READ:SITE as
  // `perms.site.canViewSite(siteId, projectId)` — the exact call this replaces (PLE-31).
  if (
    can(Actions.READ, Subjects.SITE, {
      siteId: data.SiteId,
      ...(data.ProjectId && { projectId: data.ProjectId }),
    })
  ) {
    if (boqStore.currentSiteId === data.SiteId) {
      siteStore.getSite(data.SiteId);
    } else {
      siteStore.paginatedSites = siteStore.paginatedSites.map((site) =>
        site.id === data.SiteId ? { ...site } : site,
      );
    }
  }
};

onUnmounted(() => {
  pusherStore.unsubscribeFromChannel('role-events');
  pusherStore.unsubscribeFromChannel('site-events');
  pusherStore.unsubscribeFromChannel('boq-events');
});
</script>
